import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { Button, Text, Radio, Card, Group, Container, Grid, Progress, Blockquote, Alert, Paper, Modal, Title, RingProgress, Center, SimpleGrid, Badge, Box, TextInput, PasswordInput, Divider, Loader } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { supabase } from '../supabase/client';
import { groupBy, keyBy, mapValues } from 'lodash';
import { Database, Json } from '@/database.types';
import { IconAlertCircle, IconBoxMultiple4, IconHelpCircle, IconHelpCircleFilled, IconHourglassEmpty, IconInfoCircle, IconQuestionMark, IconLock, IconMail, IconUser } from '@tabler/icons-react';
import { BookOpen, ChevronDown, HelpCircle, Info, LightbulbIcon, Lock, Mail, User, X } from 'lucide-react';
import LoadingPassages from '../components/LoadingPassages';
import { DateTime } from 'luxon';
import { Event, getCurrentEvent } from '../utils';
import BreakModal from './Test/Break';
import ReadyToStartTest from '../components/ReadyToStartTest';
import ErrorLoadingExam from '../components/ErrorLoadingExam';
import TestAlreadyTaken from './Test/AlreadyTaken';

type Answers = Database['public']['Tables']['profile_answers']['Row'];

// 1) Fetch passages for a specific test
const fetchPassages = async (testId: string, segment: number) => {
  const { data, error } = await supabase
    .from('test_questions')
    .select(`
      question_order,
      test:tests(
        id,
        level
      ),
      passages(
        id, question_type, prompt,
        correct_answer,

        questions (
          id,
          prompt,
          explanation,
          hint,
          level,

          answers!answers_question_id_fkey(
            id,
            sorted,
            content
          )
        )
      )
    `)
    .eq('test_id', testId)
    .eq('question_order', segment)
    .order('question_order');
  if (error) throw error;
  return data;
};

const hints = [
  {
    questionId: '1',
    content: "Look for the overall scope of the passage. Does it take a strong position or does it present multiple perspectives?"
  },
  {
    questionId: '2',
    content: "Review the paragraph that discusses reasons for delegation to agencies and identify what's explicitly mentioned."
  },
  {
    questionId: '3',
    content: "Consider what criticisms are attributed to opponents of administrative regulation."
  },
  {
    questionId: '4',
    content: "Evaluate whether the author presents one side more favorably than the other."
  },
  {
    questionId: '5',
    content: "Think about what evidence would support the claim that agencies are sufficiently accountable."
  },
  {
    questionId: '6',
    content: "Focus on how the passage characterizes the major questions doctrine and its implications."
  }
];

const fetchAnswers = async (testId: string, userId: string) => {
  const { data, error } = await supabase
    .from('profile_answers')
    .select(`*, answers(question_id)`)
    .eq('profile_id', userId)
    .eq('test_id', testId);
  if (error) throw error;
  return data;
};

const fetchTests = async (testId: string, userId: string) => {
  const { data, error } = await supabase
    .from('profile_tests')
    .select(`*`)
    .eq('profile_id', userId)
    .eq('id', testId).single();
  if (error) throw error;
  return data;
};

// 2) Save user answers (INSERT or UPDATE in answers table)
const saveAnswer = async (params: {
  testId: string;
  passageId: string;
  choiceId: string;
  userId: string;
  setTestExpired: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { passageId, choiceId, testId, userId, setTestExpired } = params;

  const { error } = await supabase.rpc('upsert_answer', {
    _test_id: testId,
    _question_id: passageId,
    _choice: choiceId,
  })
  
  if (error) {
    if (error.message?.includes(`Test expired`)) {
      setTestExpired(true);
      return null;
    }
  }

  return params;
};

const saveIndex = async (params: {
  testId: string;
  userId: string;
  index: number;
}) => {
  const { testId, userId, index } = params;

  const { error } = await supabase.from('profile_tests').update({
    question_index: index
  }).eq('id', testId).eq('profile_id', userId);
  if (error) throw error;

  return index;
}

// 3) Final submission: calls a RPC or simply updates all rows
const submitTestRPC = async (params: {
  testId: string;
}) => {
  const { testId } = params;

  try {
    const { error } = await supabase.rpc('submit_test', {
      _test_id: testId,
    });

    if (error) throw error;
  } catch (e) {
    showNotification({
      message: `${e?.message || e}`,
    })
  }
};

const TestPage: React.FC = () => {
  const { user, signIn, signUp } = useAuth();
  const { testId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [choice, setChoice] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [breakActive, setBreakActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState<number>(35 * 60); // default to 35 min in seconds
  const [maxTimeLeft, setMaxTimeLeft] = useState<number>(0); // default to 35 min in seconds
  const [sectionNumber, setSectionNumber] = useState<number>();
  const [testExpired, setTestExpired] = useState(false);

  // Authentication modal states
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAuthLoading, setShowAuthLoading] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [formError, setFormError] = useState('');
  
  // State for selected question and answers
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});

  const [testAlreadyCompleted, setTestAlreadyCompleted] = useState(false);


  // Animation states
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: currentTest, isLoading:isLoading3, refetch:refetchTests } = useQuery({
    queryKey: ['profile_test', testId],
    queryFn: async () => {
      const test = await fetchTests(testId ?? '', user?.id ?? '');
      if (test) {
        if (test.expiration_time && DateTime.fromISO(test.expiration_time) <= DateTime.now()) {
          if (user?.is_anonymous) {
            setShowAuthModal(true);
          }

          setTestExpired(true);

          if (test.completed_on) {
            setTestAlreadyCompleted(testAlreadyCompleted);
          }

          return test;
        }

        setCurrentIndex(test.question_index);
      }

      return test;
    },
    enabled: !!testId,
  });

  const evt = getCurrentEvent(currentTest?.started_on ?? '', currentTest?.sections as unknown as Event[]);

  const { data: passages, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['passages', testId],
    queryFn: async () => {
      const data = await fetchPassages(currentTest?.test_id ?? '', sectionNumber!);
      return data;
    },
    enabled: !!currentTest && !!currentTest?.test_id && !!sectionNumber && sectionNumber > 0,
  });

  const { data: answers, isLoading:isLoading2 } = useQuery({
    queryKey: ['answers', testId],
    queryFn: async () => {
      const answers = await fetchAnswers(testId ?? '', user?.id ?? '');
      let a = selectedAnswers;
      for (const answer of answers) {
        if (answer.answer_id && answer.answers?.question_id) {
          a[answer.answers?.question_id] = answer.answer_id;
        }
      }

      setSelectedAnswers(a);

      return keyBy(answers, 'question_id');
    },
    enabled: !!testId,
  });

  useEffect(() => {
    const passage = passages && passages[currentIndex];
    if (passage && passage.passages.questions && !selectedQuestion) {
      setSelectedQuestion(passage.passages.questions[0].id);
    }

    if (passage && answers && answers[passage?.passages?.id]) {
      setChoice(answers[passage?.passages?.id].answer_id ?? '');
    } else {
      setChoice('');
    }
  }, [answers, passages, currentIndex]);

  // useEffect(() => {
  //   if (currentTest && currentTest.question_index >= 0) {
  //     setCurrentIndex(currentTest?.question_index);
  //   }
  // }, [currentTest]);

  useEffect(() => {
    if (sectionNumber && sectionNumber > 0) {
      refetch();
      (testId && supabase.rpc('update_test', {
        _test_id: testId,
      }));
    }
  }, [sectionNumber]);

  // Animation effect when direction changes
  useEffect(() => {
    if (slideDirection) {
      setIsAnimating(true);
      
      // Reset animation state after animation completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
      }, 300); // Match this with the CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  // 2) Timer effect: runs every second
  useEffect(() => {
    if (!currentTest) return;

    const startTime = DateTime.fromISO(currentTest.started_on ?? '');
    const expirationTime = DateTime.fromISO(currentTest.expiration_time ?? '');

    if (expirationTime < startTime || !currentTest.started_on) {
      return;
    }

    const interval = setInterval(async () => {
      const now = DateTime.now();
      const exp = testExpired || (currentTest && currentTest.expiration_time && DateTime.fromISO(currentTest.expiration_time) <= DateTime.now());

      if (currentTest && !currentTest.completed_on && exp) {
        try {
          interval && clearInterval(interval);
          await handleSubmit();
        } catch (e) {}

        // Test expired
        // clearInterval(interval);
        // Possibly auto-submit or navigate away
        // setTestExpired(true);
        // navigate('/test-submitted/' + testId);
        // handleSubmit();
      } else {
        // figure out current section, break, timeLeft in the section
        const elapsed = now.diff(startTime, 'second');
        const evt = getCurrentEvent(startTime.toString(), currentTest.sections as unknown as Event[]);
        const isBreak = evt?.event?.type === "break";
        const section = evt?.section ?? 1;
        const maxTime = (evt?.event?.duration ?? 0) * 1_000 * 60;
        const totalTime = ((currentTest.sections as unknown as Event[]) ?? []).reduce(
          (p, c, i) => {
            return p + ((i <= (evt?.index ?? 0)) ? (c.duration ?? 0) : 0);
          }, 0) * 60;

        const sectionTimeLeft = totalTime - Math.floor(elapsed.seconds);

        setBreakActive(isBreak);
        setTimeLeft(Math.max(0, sectionTimeLeft));
        setMaxTimeLeft(maxTime);
        if (sectionNumber !== section) {
          // setCurrentIndex(currentTest.question_index);
          setSectionNumber(section);
        }
      }
    }, 1_000);

    return () => clearInterval(interval);
  }, [currentTest, navigate, testId]);

  const saveAnswerMutation = useMutation({
    mutationFn: saveAnswer,
    onSuccess: (params) => {
      if (!params) {
        return;
      }

      const { choiceId:id, passageId:questionId } = params;

      setChoice(id);

      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['answers'] })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (oldData: Record<string, Answers>) => {
            const a: Answers = {
              answer_id: id,
              id: questionId,
              profile_id: user?.id ?? '',
              test_id: passage?.test?.id ?? '',
              answered_on: new Date().toISOString(),
              question_id: questionId,
            };

            if (oldData[id]) {
              return mapValues(oldData, v => ({
                ...v,
                answer_id: v.id === questionId ? id : v.answer_id,
              }));
            }

            return {
              ...oldData,
              [questionId]: a,
            }
          })
        })
    }
  });
  const updateTestMutation = useMutation({ mutationFn: saveIndex, onSuccess: (index) => {
    setCurrentIndex(index);
  } });
  const submitTestMutation = useMutation({
    mutationFn: submitTestRPC,
    onError: (err) => {
      showNotification({
        message: `${err}`,
      })
    }
  });

  const handlePrev = () => {
    if (breakActive || isAnimating || !passages) return;

    const i = Math.max(0, currentIndex-1);
    const passage = passages[i];

    if (passage && passage.question_order === sectionNumber) {
      setSlideDirection('right'); // Slide right for previous
      
      // Set a small delay to ensure animation appears before changing content
      setTimeout(() => {
        setShowExplanation(false);
        setShowHint(false);
        setChoice('');
        setCurrentIndex(i);

        updateTestMutation.mutate({
          testId: testId ?? '',
          userId: user?.id ?? '',
          index: i,
        });
      }, 50);
    }
  };

  const handleNext = () => {
    if (breakActive || isAnimating || !passages) return;

    const i = (currentIndex < passages.length - 1 ? currentIndex + 1 : currentIndex)
    const passage = passages[i];

    // if (passage && passage.question_order === sectionNumber) {
      setSlideDirection('left'); // Slide left for next
      // Set a small delay to ensure animation appears before changing content
      setTimeout(() => {
        setShowExplanation(false);
        setShowHint(false);
        setChoice('');
        setCurrentIndex(i);
        setSelectedQuestion('');

        updateTestMutation.mutate({
          testId: testId ?? '',
          userId: user?.id ?? '',
          index: i,
        });
      }, 50);
    // }
  };

  const handleSubmit = async () => {
    if (!testId) return;
    
    // Check if user is logged in
    if (!user || user.is_anonymous) {
      setShowAuthModal(true);
      return;
    }
    
    try {
      await submitTestMutation.mutateAsync({
        testId,
      });

      navigate(`/test-submitted/${testId}`);
    } catch (err) {
      showNotification({
        title: 'Error Submitting Test',
        message: (err as Error).message,
        color: 'red',
      });
    }
  };
  
  // Toggle between login and register forms
  const toggleAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
    setFormError('');
  };
  
  // Handle login submission
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    try {
      const { error } = await signIn(email, password);

      if (error) {
        // showNotification({
        //   title: 'Login Error',
        //   message: error,
        //   color: 'red',
        // });
        // return;
        throw error;
      }

      setShowAuthLoading(true);
      await handleSubmit();
      setShowAuthModal(false);
    } catch (err) {
      setFormError((err as Error).message || 'Login failed. Please try again.');
    } finally {
      setShowAuthLoading(false);
    }
  };
  
  // Handle registration submission
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!name) {
      setFormError('Name is required');
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({
        email,
        password,
        data: {
          full_name: name,
        }
      });
      if (error) {
        throw error;
      }

      showNotification({
        title: 'Registration Successful',
        message: 'Your account has been created!',
        color: 'green',
      });
      
      setShowAuthLoading(true);
      await handleSubmit();
      setShowAuthModal(false);
    } catch (err) {
      setFormError((err as Error).message || 'Registration failed. Please try again.');
    } finally {
      setShowAuthLoading(true);
    }
  };

  // Animation classes based on slide direction
  const animationClass = slideDirection 
    ? slideDirection === 'left' 
      ? 'animate-slide-left'
      : 'animate-slide-right'
    : '';

  // Card deck functionality
  const [cardDeck, setCardDeck] = useState([
    { id: '1', text: "Look for keywords in the passage that directly relate to the question", flipped: false, drawn: false },
    { id: '2', text: "Eliminate answers that contradict information in the passage", flipped: false, drawn: false },
    { id: '3', text: "Don't bring outside knowledge to the question - rely only on the passage", flipped: false, drawn: false },
    { id: '4', text: "Focus on the author's tone and attitude throughout the passage", flipped: false, drawn: false },
    { id: '5', text: "Pay attention to qualifying words like 'always,' 'never,' 'some,' 'most'", flipped: false, drawn: false },
    { id: '6', text: "Look for the function of each paragraph in the overall passage", flipped: false, drawn: false },
    { id: '7', text: "Consider how new information in the passage modifies earlier statements", flipped: false, drawn: false },
    { id: '8', text: "Identify the main conclusion and how each paragraph supports it", flipped: false, drawn: false },
    { id: '9', text: "Pay attention to transitions between paragraphs for shifts in argument", flipped: false, drawn: false }
  ]);
  
  const [drawnCards, setDrawnCards] = useState<{ id: string; text: string; flipped: boolean; drawn: boolean; }[]>([]);
  const [canDraw, setCanDraw] = useState(true);
  // Dark mode state
  const [darkMode, setDarkMode] = useState(false);
  
  // Collapsible sections state
  const [hintsExpanded, setHintsExpanded] = useState(true);
  const [cardsExpanded, setCardsExpanded] = useState(true);

  // Draw three cards
  const drawCards = () => {
    if (!canDraw) return;
    
    const remainingCards = cardDeck.filter(card => !card.drawn);
    if (remainingCards.length === 0) {
      // Reset deck
      setCardDeck(prevDeck => prevDeck.map(card => ({ ...card, drawn: false, flipped: false })));
      setDrawnCards([]);
      return;
    }
    
    // Shuffle and draw up to 3 cards
    const shuffled = [...remainingCards].sort(() => 0.5 - Math.random());
    const newDrawn = shuffled.slice(0, Math.min(3, shuffled.length));
    
    // Update card deck
    setCardDeck(prevDeck => 
      prevDeck.map(card => 
        newDrawn.some(drawnCard => drawnCard.id === card.id) 
          ? { ...card, drawn: true } 
          : card
      )
    );
    
    // Set drawn cards
    setDrawnCards(newDrawn.map(card => ({ ...card })));
    
    // Cannot draw again until cards are viewed
    setCanDraw(false);
  };
  
  // Flip a card
  const flipCard = (cardId: string) => {
    setDrawnCards(prevDrawnCards => 
      prevDrawnCards.map(card => 
        card.id === cardId ? { ...card, flipped: !card.flipped } : card
      )
    );
    
    // If all cards are flipped, allow drawing again
    const updatedDrawnCards = drawnCards.map(card => 
      card.id === cardId ? { ...card, flipped: !card.flipped } : card
    );
    
    if (updatedDrawnCards.every(card => card.flipped)) {
      setCanDraw(true);
    }
  };


  // Function to handle answer selection
  const selectAnswer = (questionId: string, answerIndex: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };


  const AuthModal = () => {
    if (!showAuthModal) return <></>;

    return (
        <>
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto">
            {/* Modal Container */}
            <div className="relative w-full max-w-md mx-auto bg-white rounded-xl shadow-2xl transform transition-all duration-300 ease-in-out">
              {/* Glass effect top bar */}
              <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-t-xl"></div>
  
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 pt-6 pb-2">
                <h3 className="text-xl font-bold text-gray-800">
                  {authMode === 'login' ? 'Welcome Back' : 'Create Your Account'}
                </h3>
                <button 
                  onClick={() => setShowAuthModal(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="px-6 py-4">
                <p className="text-gray-600 text-sm mb-6">
                  {authMode === 'login' 
                    ? 'Sign in to save your test progress and access your results.' 
                    : 'Register to track your progress and view comprehensive analytics.'}
                </p>
                
                {formError && (
                  <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <IconAlertCircle className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {authMode === 'login' ? (
                  <form onSubmit={handleLogin} className="space-y-5">
                    {/* Email Field */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="email"
                          type="email"
                          required
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Password Field */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="password"
                          type="password"
                          required
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex items-center justify-between pt-2">
                      <button 
                        type="button"
                        onClick={toggleAuthMode}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Need an account?
                      </button>
                      
                      <button 
                        type="submit"
                        className="inline-flex justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-150"
                      >
                        Login
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-5">
                    {/* Name Field */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="name"
                          type="text"
                          required
                          placeholder="John Doe"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Email Field */}
                    <div>
                      <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="reg-email"
                          type="email"
                          required
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Password Field */}
                    <div>
                      <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="reg-password"
                          type="password"
                          required
                          placeholder="Choose a strong password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex items-center justify-between pt-2">
                      <button 
                        type="button"
                        onClick={toggleAuthMode}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Already have an account?
                      </button>
                      
                      <button 
                        type="submit"
                        className="inline-flex justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-150"
                      >
                        Register
                      </button>
                    </div>
                  </form>
                )}
                
                {/* Divider */}
                {/* <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div> */}
                
                {/* Social Buttons */}
                {/* <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                  >
                    <svg className="h-5 w-5 mr-2" fill="#4285F4" viewBox="0 0 24 24">
                      <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
                    </svg>
                    Google
                  </button>
                  <button
                    type="button"
                    className="py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                  >
                    <svg className="h-5 w-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                      <path d="M9.677 20.895v-7.745H7.273V9.795h2.404V7.598c0-2.32 1.417-3.58 3.488-3.58.994 0 1.848.072 2.095.105v2.429h-1.433c-1.128 0-1.348.534-1.348 1.32v1.921h2.698l-.35 3.354h-2.348v7.75H9.677z" />
                    </svg>
                    Facebook
                  </button>
                </div> */}
                
                {/* Terms */}
                <div className="mt-6 text-center text-xs text-gray-500">
                  <p>By continuing, you agree to our 
                    <a target="_blank" href="/terms-of-service" className="text-indigo-600 hover:text-indigo-500"> Terms of Service </a> 
                    and 
                    <a target="_blank" href="/privacy" className="text-indigo-600 hover:text-indigo-500"> Privacy Policy</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
    )
  }

  if (currentTest && (!currentTest?.started_on || currentTest?.started_on?.toString().trim() === "")) {
    return <ReadyToStartTest onStartTest={async () => {
      const { error } = await supabase.rpc('start_test', {
        _test_id: currentTest.id,
      });

      await refetchTests();
    }} />;
  }

  if (isError) return <ErrorLoadingExam />;

  if (isLoading || isLoading2 || isLoading3) // || sectionNumber === undefined || ((!passages || passages.length === 0) && maxTimeLeft < 1))
    return <LoadingPassages />;

  if (currentTest && currentTest.started_on && currentTest.expiration_time && DateTime.fromISO(currentTest.expiration_time) <= DateTime.now() && (testAlreadyCompleted || currentTest.score !== null)) {
    return <TestAlreadyTaken currentTest={currentTest} />;
  }

  if (testExpired) {
    return (<>
      <AuthModal />
      <div className="flex items-center justify-center z-10 p-4">
        <div className="relative bg-white rounded-2xl w-full max-w-md p-6 md:p-8">
          <div className="text-center">
            <div className="bg-gradient-to-r from-pink-500 to-orange-500 w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 animate-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Test Session Expired</h2>
            <p className="mb-6 text-gray-600">
              Your test session has expired. 
              All progress has been saved automatically.
            </p>
            <div className="flex flex-col space-y-3">
              <button 
                className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium rounded-lg px-6 py-3 transition-all duration-300 hover:scale-105 focus:outline-none"
                onClick={() => navigate('/dashboard')}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
          {/* <button
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-all duration-300 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button> */}
        </div>
      </div>
    </>);
  }

  if (!passages || passages.length === 0) return <ErrorLoadingExam />

  const passage = passages[currentIndex];
  const questions = passage.passages.questions;

  const handleAnswerChange = (choiceId: string) => {
    if (!user || breakActive || !passages) return;

    const passageId = passages[currentIndex].passages.id;
    saveAnswerMutation.mutate({
        testId: testId ?? '',
        passageId,
        choiceId,
        userId: user.id,
        setTestExpired,
     });
     setQuestionIndex(questionIndex+1 > questions.length ? 0 : questionIndex + 1);
  };

  return ( <>
    <style jsx="true">{`
       @keyframes slideLeft {
         0% { transform: translateX(100%); opacity: 0; }
         100% { transform: translateX(0); opacity: 1; }
       }
      
       @keyframes slideRight {
         0% { transform: translateX(-100%); opacity: 0; }
         100% { transform: translateX(0); opacity: 1; }
       }
      
       .animate-slide-left {
         animation: slideLeft 300ms ease-out forwards;
       }
      
       .animate-slide-right {
         animation: slideRight 300ms ease-out forwards;
       }
      
       .question-container {
         overflow: hidden;
         position: relative;
       }
      
       .question-content {
         will-change: transform;
         transform-origin: center center;
       }

      @keyframes spin-slow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes ping-slow {
        0% {
          transform: scale(1);
          opacity: 1;
        }
        75%, 100% {
          transform: scale(2);
          opacity: 0;
        }
      }

      .animate-spin-slow {
        animation: spin-slow 8s linear infinite;
      }

      .animate-ping-slow {
        animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
      }

      .delay-300 {
        animation-delay: 300ms;
      }

      .delay-700 {
        animation-delay: 700ms;
      }
     `}</style>
    {/* Modal for break time */}
    { breakActive && <BreakModal />
    }
    <AuthModal />
    {/* <div className="grid grid-1 content-between"> */}
    {!breakActive && (
      <div className="overflow-hidden grid grid-cols md:grid-cols-3 grid-cols-1">
        {/* Left column - Passage */}
        <div className={`p-4 overflow-y-auto ${darkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white border-r border-gray-200'}`}>
          <div>
            <div className="flex items-center mb-3">
              <BookOpen className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} mr-2`} />
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>Passage</h2>
            </div>
            <div className={`prose ${darkMode ? 'prose-invert' : 'prose-indigo'}`}>
              {passage.passages.prompt.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-800'} leading-relaxed`}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
        
        {/* Middle column - Questions */}
        <div className={`p-4 sm:pb-12 overflow-y-auto ${darkMode ? 'bg-gray-900 border-r border-gray-700' : 'bg-gray-50 border-r border-gray-200'}`}>
          <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-indigo-300' : 'text-indigo-900'}`}>Questions</h2>
          <div className="space-y-4">
            {passage.passages.questions.map((question, index) => (
              <div 
                key={question.id}
                className={`p-4 rounded-lg transition-all duration-200 cursor-pointer ${
                  selectedQuestion === question.id 
                    ? darkMode 
                      ? 'bg-gradient-to-r from-indigo-800 to-purple-800 text-white shadow-md transform scale-102' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md transform scale-102'
                    : darkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-200 shadow-sm'
                      : 'bg-white hover:bg-indigo-50 text-gray-800 shadow-sm'
                }`}
                onClick={() => setSelectedQuestion(question.id)}
              >
                <h3 className={`font-medium ${selectedQuestion === question.id ? 'text-white' : darkMode ? 'text-indigo-300' : 'text-indigo-900'}`}>
                  Question {index+1}
                </h3>
                <p className={`mt-2 ${selectedQuestion === question.id ? 'text-white' : darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {question.prompt}
                </p>
                <div className="mt-3 space-y-2">
                  {question.answers.map((option, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => {selectAnswer(question.id, option.id); handleAnswerChange(option.id); }}
                      className={`flex items-start p-2 rounded cursor-pointer transition-all duration-200 ${
                        selectedQuestion === question.id 
                          ? selectedAnswers[question.id] === option.id
                            ? 'bg-white/30 shadow-inner' 
                            : 'bg-white/10 hover:bg-white/25 transform hover:translate-x-1'
                          : selectedAnswers[question.id] === option.id
                            ? darkMode ? 'bg-indigo-900 shadow-inner' : 'bg-indigo-100 shadow-inner' 
                            : darkMode ? 'hover:bg-gray-700 hover:shadow-sm transform hover:translate-x-1' : 'hover:bg-gray-100 hover:shadow-sm transform hover:translate-x-1'
                      }`}
                    >
                      <div className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center mr-2 ${
                        selectedQuestion === question.id 
                          ? selectedAnswers[question.id] === option.id
                            ? 'border-white bg-white text-indigo-600 font-bold'
                            : 'border-white text-white' 
                          : selectedAnswers[question.id] === option.id
                            ? darkMode ? 'border-indigo-400 bg-indigo-400 text-gray-900 font-bold' : 'border-indigo-500 bg-indigo-500 text-white font-bold'
                            : darkMode ? 'border-indigo-400 text-indigo-300' : 'border-indigo-300 text-indigo-800'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <p className={`${
                        selectedQuestion === question.id 
                          ? 'text-white' 
                          : selectedAnswers[question.id] === option.id
                            ? darkMode ? 'text-indigo-300 font-medium' : 'text-indigo-800 font-medium'
                            : darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {option.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Right column - Hints and Cards */}
        <div className={`p-4 pb-12 sm:pb-4 overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Hints section */}
          <div className={`mb-6 ${darkMode ? 'bg-gradient-to-br from-amber-800 to-orange-900' : 'bg-gradient-to-br from-amber-50 to-orange-50'} p-5 rounded-xl shadow-sm overflow-hidden`}>
            <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => setHintsExpanded(!hintsExpanded)}>
              <div className="flex items-center">
                <HelpCircle className={`w-5 h-5 ${darkMode ? 'text-amber-300' : 'text-amber-600'} mr-2`} />
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>Hints & Explanations</h2>
              </div>
              <ChevronDown 
                className={`w-5 h-5 ${darkMode ? 'text-amber-300' : 'text-amber-600'} transform transition-transform duration-300 ${hintsExpanded ? 'rotate-180' : ''}`} 
              />
            </div>
            
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${hintsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              {hints
                .filter(hint => hint.questionId === selectedQuestion)
                .map(hint => (
                  <div key={hint.questionId} className={`${darkMode ? 'bg-gray-800 border border-amber-700' : 'bg-white'} rounded-lg p-4 shadow-sm`}>
                    <p className={darkMode ? 'text-gray-100' : 'text-gray-800'}>{hint.content}</p>
                  </div>
                ))}
            </div>
          </div>
          
          {/* Card deck section */}
          <div className={`${darkMode ? 'bg-gradient-to-br from-teal-800 to-teal-950' : 'bg-gradient-to-br from-teal-50 to-emerald-50'} p-5 rounded-xl shadow-sm overflow-hidden`}>
            <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => setCardsExpanded(!cardsExpanded)}>
              <div className="flex items-center">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-teal-200' : 'text-teal-800'}`}>Strategy Cards</h2>
              </div>
              <div className="flex items-center">
                {cardsExpanded && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent collapsing when clicking the button
                      drawCards();
                    }}
                    disabled={!canDraw && drawnCards.length > 0}
                    className={`mr-3 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      canDraw 
                        ? darkMode
                          ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow hover:shadow-md transform hover:-translate-y-1'
                          : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow hover:shadow-md transform hover:-translate-y-1' 
                        : darkMode
                          ? 'bg-gray-700 text-gray-400'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {drawnCards.length === 0 ? 'Draw Cards' : 'New Hand'}
                  </button>
                )}
                <ChevronDown 
                  className={`w-5 h-5 ${darkMode ? 'text-teal-300' : 'text-teal-600'} transform transition-transform duration-300 ${cardsExpanded ? 'rotate-180' : ''}`} 
                />
              </div>
            </div>
            
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${cardsExpanded ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
              {/* Drawn cards */}
              <div className="flex justify-center space-x-4 h-48">
                {drawnCards.length > 0 ? (
                  drawnCards.map(card => (
                    <div 
                      key={card.id}
                      className={`relative w-1/3 h-full rounded-lg shadow-md cursor-pointer transform transition-all duration-500 ${
                        card.flipped ? 'rotate-y-180' : ''
                      } ${canDraw ? 'opacity-70' : 'hover:shadow-lg'}`}
                      onClick={() => !canDraw && !card.flipped && flipCard(card.id)}
                      style={{ perspective: '1000px' }}
                    >
                      {/* Card back */}
                      <div 
                        className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-teal-500 to-emerald-600' : 'bg-gradient-to-br from-teal-500 to-emerald-600'} rounded-lg flex items-center justify-center transition-all duration-500 backface-hidden ${
                          card.flipped ? 'opacity-0' : 'opacity-100'
                        }`}
                      >
                        <div className="text-white font-bold text-2xl">?</div>
                      </div>
                      
                      {/* Card front */}
                      <div 
                        className={`absolute inset-0 ${darkMode ? 'bg-gray-800 border border-teal-700' : 'bg-white'} p-4 rounded-lg flex items-center justify-center transition-all duration-500 backface-hidden rotate-y-180 ${
                          card.flipped ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <p className={`text-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{card.text}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`flex items-center justify-center w-full h-full ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    <p>Draw cards to reveal study strategies!</p>
                  </div>
                )}
              </div>
              
              {/* Remaining cards indicator */}
              <div className={`mt-4 text-center text-sm ${darkMode ? 'text-teal-200' : 'text-gray-600'}`}>
                {cardDeck.filter(card => !card.drawn).length} cards remaining in deck
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
      {/* Fixed Status Bar */}
      <div className="w-full">
      { !breakActive && 
        <Box className="flex justify-between bg-slate-50 border-t border-slate-100 py-4 px-20">
              <button 
                className="px-6 py-2 rounded-lg bg-linear-to-r to-indigo-600 from-purple-600 text-white font-medium text-lg flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg disabled:opacity-40"
                onClick={handlePrev} 
                disabled={currentIndex < 1 || !passages[currentIndex-1] || passages[currentIndex-1].question_order !== sectionNumber || isAnimating}
              >
                Previous Passage
              </button>
              {currentIndex < passages.length - 1 ? (
                <button
                  className="px-6 py-2 rounded-lg bg-linear-to-r from-indigo-600 to-purple-600 text-white font-medium text-lg flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg disabled:opacity-40" 
                  disabled={!passages[currentIndex+1] || (passages[currentIndex+1] && passages[currentIndex+1]?.question_order !== sectionNumber) || isAnimating} 
                  onClick={handleNext}
                >
                  Next Passage
                </button>
              ) : (
                (passage.test.level === 'free' || (sectionNumber ?? 0) >= 4) && 
                <button
                  className="px-6 py-2 rounded-lg bg-linear-to-r from-green-500 to-lime-500 text-white font-medium text-lg flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg disabled:opacity-40" 
                  onClick={handleSubmit}
                >
                  Submit Test
                </button>
                // <Button 
                //   color="green" 
                //   className="border-slate-200 hover:bg-slate-100 text-slate-600" 
                //   onClick={handleSubmit}
                // >
                //   Submit Test
                // </Button>
              )}
        </Box>
      }
      <div className="bg-linear-to-r from-indigo-50 via-purple-50 to-indigo-50 border-t border-gray-200 shadow-lg relative pt-8 pb-6 px-4 sm:px-6">
        {/* Top-centered clock */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="bg-white rounded-full p-2 shadow-lg" style={{ width: '88px', height: '88px' }}>
                <div className="relative inline-flex items-center justify-center -left-[4px] -top-[3px]">
                  {/* Circular progress */}
                  <svg className="w-20 h-20" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e6e6e6"
                      strokeWidth="2.5"
                      strokeDasharray="100, 100"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={timeLeft < 1 ? "#ef4444" : "#6366f1"}
                      strokeWidth="2.5"
                      strokeDasharray={`${(((maxTimeLeft / 1_000)-timeLeft) / (maxTimeLeft / 1_000)) * 100}, 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  {/* Timer text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className={`font-mono text-xl font-bold ${timeLeft < 1 ? "text-red-500" : "text-indigo-600"}`}>
                        {formatTime(timeLeft)}
                      </div>
                    </div>
                  </div>
                </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 items-center mt-2">
          <div className="flex flex-col">
            <h3 className="text-gray-800 font-medium text-lg">LSAT Beginner Test</h3>
            <p className="text-gray-600 text-sm">{evt?.event?.category}</p>
            
            <div className="flex items-center space-x-3 mt-3">
              <span className="text-xs text-indigo-600 font-medium bg-indigo-50 px-3 py-1 rounded-full">
                {breakActive ? 'On Break' : `Section ${evt?.section} of ${(currentTest?.sections as unknown as Event[]).filter(x => x.type === "section").length ?? 0}`}
              </span>
              <span className="text-xs text-purple-600 font-medium bg-purple-50 px-3 py-1 rounded-full">
                { breakActive ? '' : <>Difficulty: <span className="capitalize">{passage.test?.level}</span></> }
              </span>
            </div>
          </div>
          {
            !breakActive && 
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-1 text-sm">
                  <span className="text-gray-700 font-medium">Progress</span>
                  <span className="text-indigo-600 font-medium">{breakActive ? 'On Break' : `${currentIndex + 1}/${passages?.length} Passages`}</span>
                </div>
                
                <div className="bg-white rounded-full h-3 overflow-hidden shadow-inner">
                  <div 
                    className="bg-linear-to-r from-indigo-500 to-purple-600 h-full rounded-full relative transition-all duration-300 ease-out"
                    style={{ width: `${((currentIndex + 1) / passages?.length) * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 overflow-hidden" style={{
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.2) 50%, rgba(255,255,255,.2) 75%, transparent 75%, transparent)',
                      backgroundSize: '1rem 1rem'
                    }}></div>
                  </div>
                </div>
                
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Start</span>
                  <span>50%</span>
                  <span>Finish</span>
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  </>);
};

const HighlightedText = ({ text, highlights }: { text: string, highlights: string[] }) => {
  if (!highlights || highlights.length === 0) {
    return <span>{text}</span>;
  }

  let result = text;
  highlights.forEach(highlight => {
    const regex = new RegExp(`(${highlight})`, 'g');
    result = result.replace(regex, '<span class="bg-teal-50 text-teal-700 px-1 rounded">$1</span>');
  });

  return <span dangerouslySetInnerHTML={{ __html: result }} />;
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default TestPage;
